import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import { FaComments, FaTimes, FaPaperPlane, FaWhatsapp, FaPhone, FaEnvelope, FaCheckCircle } from "react-icons/fa";
import { startConversation, accessConversation, getSupportWsUrl } from "../services/supportApi";

const SUPPORT_STORAGE_KEY = "afriq_support_visitor";
const SUPPORT_STORAGE_DAYS = 30;

function getStoredVisitor() {
  try {
    const raw = localStorage.getItem(SUPPORT_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.email || !data.pin) return null;
    if (data.expiresAt && Date.now() > data.expiresAt) {
      localStorage.removeItem(SUPPORT_STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setStoredVisitor({ email, pin, conversationId }) {
  try {
    const expiresAt = Date.now() + SUPPORT_STORAGE_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(
      SUPPORT_STORAGE_KEY,
      JSON.stringify({ email, pin, conversationId, expiresAt })
    );
  } catch (_) {}
}

function clearStoredVisitor() {
  try {
    localStorage.removeItem(SUPPORT_STORAGE_KEY);
  } catch (_) {}
}

const apiMessageToDisplay = (m) => ({
  id: m.id,
  text: m.content,
  sender: m.sender === "admin" ? "support" : "user",
  timestamp: m.created_at ? new Date(m.created_at) : new Date(),
});

export default function FloatingChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [chatStarted, setChatStarted] = useState(false);
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [message, setMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPinField, setShowPinField] = useState(false);
  const [pinFromStart, setPinFromStart] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [adminOnline, setAdminOnline] = useState(false);
  const [error, setError] = useState("");
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // When opening the widget, restore from localStorage and load conversation if stored (within 30 days)
  const hasTriedRestore = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasTriedRestore.current = false;
      return;
    }
    if (chatStarted) return;
    const stored = getStoredVisitor();
    if (!stored?.email || !stored?.pin) return;
    if (hasTriedRestore.current) return;
    hasTriedRestore.current = true;
    setEmail(stored.email);
    setPin(stored.pin);
    setLoading(true);
    setError("");
    accessConversation(stored.email, stored.pin)
      .then((res) => {
        const conv = res.conversation || res;
        setConversationId(conv.id);
        setPinFromStart(null);
        setMessages((conv.messages || []).map(apiMessageToDisplay));
        setChatStarted(true);
        setStoredVisitor({ email: stored.email, pin: stored.pin, conversationId: conv.id });
      })
      .catch(() => {
        clearStoredVisitor();
        setEmail("");
        setPin("");
        setError("Session expired. Please enter your details again.");
      })
      .finally(() => setLoading(false));
  }, [isOpen, chatStarted]);

  const connectWebSocket = useCallback((convId, visitorEmail, visitorPin) => {
    const url = getSupportWsUrl(convId, visitorEmail, visitorPin);
    if (!url) return;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setError("");
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        switch (data.type) {
          case "message":
            // Only add messages from admin here; visitor messages are added optimistically on send
            if (data.message && data.message.sender === "admin") {
              setMessages((prev) => [...prev, apiMessageToDisplay(data.message)]);
            }
            break;
          case "typing":
            // Only show "Support is typing" when the admin is typing, not the visitor
            setIsTyping(data.is_typing === true && data.who === "admin");
            break;
          case "read":
            // Optional: mark message as read in UI
            break;
          case "online":
            setAdminOnline(data.is_online === true);
            break;
          default:
            break;
        }
      } catch (_) {}
    };

    ws.onerror = () => setError("Connection error");
    ws.onclose = () => {
      wsRef.current = null;
      setAdminOnline(false);
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, []);

  const sendWs = useCallback((payload) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
    }
  }, []);

  useEffect(() => {
    if (!chatStarted || !conversationId || !email) return;
    const visitorPin = pinFromStart !== null ? pinFromStart : pin;
    if (!visitorPin) return;
    const cleanup = connectWebSocket(conversationId, email, visitorPin);
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      cleanup?.();
    };
  }, [chatStarted, conversationId, email, pin, pinFromStart, connectWebSocket]);

  const handleStartConversation = async () => {
    if (!email.trim()) return;
    setError("");
    setLoading(true);
    try {
      const res = await startConversation(email);
      setConversationId(res.conversation_id);
      setPinFromStart(res.pin);
      setShowPinField(false);
      setStoredVisitor({ email: email.trim(), pin: res.pin, conversationId: res.conversation_id });
    } catch (err) {
      if (err.statusCode === 400 || err.statusCode >= 400) {
        setShowPinField(true);
        setError(err.message || "Enter your PIN to continue this conversation.");
      } else {
        setError(err.message || "Could not start conversation.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccessConversation = async () => {
    if (!email.trim() || !pin.trim()) return;
    setError("");
    setLoading(true);
    try {
      const res = await accessConversation(email, pin);
      const conv = res.conversation || res;
      setConversationId(conv.id);
      setPinFromStart(null);
      const list = (conv.messages || []).map(apiMessageToDisplay);
      setMessages(list);
      setChatStarted(true);
      setStoredVisitor({ email: email.trim(), pin: pin.trim(), conversationId: conv.id });
    } catch (err) {
      setError(err.message || "Invalid email or PIN.");
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToChat = () => {
    if (conversationId && email && pinFromStart !== null) {
      setStoredVisitor({ email, pin: pinFromStart, conversationId });
    }
    setChatStarted(true);
  };

  const handleSendMessage = () => {
    if (!message.trim()) return;
    const userMessage = {
      id: `temp-${Date.now()}`,
      text: message,
      sender: "user",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    sendWs({ type: "send_message", content: message.trim() });
  };

  const handleTypingChange = (isTyping) => {
    sendWs({ type: "typing", is_typing: isTyping });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        sendWs({ type: "typing", is_typing: false });
        typingTimeoutRef.current = null;
      }, 3000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (chatStarted) {
        handleSendMessage();
      } else if (showPinField) {
        handleAccessConversation();
      } else {
        handleStartConversation();
      }
    }
  };

  const resetChat = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setChatStarted(false);
    setEmail("");
    setPin("");
    setMessage("");
    setMessages([]);
    setLoading(false);
    setShowPinField(false);
    setPinFromStart(null);
    setConversationId(null);
    setAdminOnline(false);
    setError("");
  };

  const openWhatsApp = () => {
    const phoneNumber = "237679235785";
    const text = "Hello! I need support with AFRIQ Bamenda.";
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(text)}`, "_blank");
  };
  const openPhone = () => window.open("tel:+237679235785", "_self");
  const openEmail = () => window.open("mailto:info@afriqcm.com", "_self");

  const inPinStep = pinFromStart !== null && !chatStarted;

  return (
    <FloatingChatContainer>
      <ChatWidget isOpen={isOpen}>
        <ChatHeader>
          <ChatHeaderContent>
            <ChatAvatar>
              <img src="/logo.png" alt="AFRIQ Logo" />
            </ChatAvatar>
            <ChatInfo>
              <ChatTitle>AFRIQ Support</ChatTitle>
              <ChatStatus>{adminOnline ? "Online now" : "We'll reply soon"}</ChatStatus>
            </ChatInfo>
          </ChatHeaderContent>
          <CloseButton
            onClick={() => {
              setIsOpen(false);
              resetChat();
            }}
          >
            <FaTimes />
          </CloseButton>
        </ChatHeader>

        <ChatBody>
          {!chatStarted ? (
            <>
              <WelcomeMessage>
                <MessageBubble>
                  <MessageText>
                    👋 Hello! Welcome to AFRIQ Bamenda. To start chatting with our support team, please provide your email address.
                  </MessageText>
                  <MessageTime>Just now</MessageTime>
                </MessageBubble>
              </WelcomeMessage>

              {inPinStep ? (
                <PinStep>
                  <PinMessage>
                    <strong>Save your PIN:</strong> Use this PIN when you want to continue this conversation later.
                  </PinMessage>
                  <PinDisplay>{pinFromStart}</PinDisplay>
                  <StartChatButton onClick={handleContinueToChat}>
                    <FaCheckCircle />
                    <span>Continue to chat</span>
                  </StartChatButton>
                </PinStep>
              ) : (
                <EmailForm>
                  <EmailInput
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter your email address..."
                    disabled={loading}
                  />
                  {showPinField && (
                    <PinInput
                      type="text"
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Enter your PIN"
                      maxLength={6}
                      disabled={loading}
                    />
                  )}
                  {error && <ErrorText>{error}</ErrorText>}
                  <StartChatButton
                    onClick={showPinField ? handleAccessConversation : handleStartConversation}
                    disabled={
                      !email.trim() ||
                      loading ||
                      (showPinField && !pin.trim())
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner />
                        <span>{showPinField ? "Accessing..." : "Starting..."}</span>
                      </>
                    ) : (
                      <>
                        <FaCheckCircle />
                        <span>{showPinField ? "Access conversation" : "Start conversation"}</span>
                      </>
                    )}
                  </StartChatButton>
                </EmailForm>
              )}

              <QuickActions>
                <QuickActionButton onClick={openWhatsApp}>
                  <FaWhatsapp />
                  <span>WhatsApp</span>
                </QuickActionButton>
                <QuickActionButton onClick={openPhone}>
                  <FaPhone />
                  <span>Call Us</span>
                </QuickActionButton>
                <QuickActionButton onClick={openEmail}>
                  <FaEnvelope />
                  <span>Email</span>
                </QuickActionButton>
              </QuickActions>
            </>
          ) : (
            <>
              {error && <ErrorText>{error}</ErrorText>}
              <MessagesContainer>
                {messages.map((msg) => (
                  <MessageWrapper key={msg.id} isUser={msg.sender === "user"}>
                    <MessageBubble isUser={msg.sender === "user"}>
                      <MessageText>{msg.text}</MessageText>
                      <MessageTime isUser={msg.sender === "user"}>
                        {msg.timestamp instanceof Date
                          ? msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                          : new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </MessageTime>
                    </MessageBubble>
                  </MessageWrapper>
                ))}
                {isTyping && (
                  <MessageWrapper isUser={false}>
                    <TypingIndicator>
                      <TypingDots>
                        <span></span>
                        <span></span>
                        <span></span>
                      </TypingDots>
                      <MessageTime>Support is typing...</MessageTime>
                    </TypingIndicator>
                  </MessageWrapper>
                )}
                <div ref={messagesEndRef} />
              </MessagesContainer>

              <ChatInput>
                <MessageInput
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    handleTypingChange(true);
                  }}
                  onBlur={() => handleTypingChange(false)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                />
                <SendButton onClick={handleSendMessage} disabled={!message.trim()}>
                  <FaPaperPlane />
                </SendButton>
              </ChatInput>
            </>
          )}
        </ChatBody>
      </ChatWidget>

      <FloatingButton onClick={() => setIsOpen(!isOpen)} isOpen={isOpen}>
        {isOpen ? <FaTimes /> : <FaComments />}
        {!isOpen && <NotificationDot />}
      </FloatingButton>
    </FloatingChatContainer>
  );
}

const FloatingChatContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  
  @media (max-width: 768px) {
    bottom: 15px;
    right: 15px;
  }
`;

const FloatingButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #4b2e83;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(75, 46, 131, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 12px 35px rgba(75, 46, 131, 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
`;

const NotificationDot = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 12px;
  height: 12px;
  background: #10b981;
  border-radius: 50%;
  border: 2px solid white;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.2); opacity: 0.7; }
    100% { transform: scale(1); opacity: 1; }
  }
`;

const ChatWidget = styled.div`
  position: absolute;
  bottom: 80px;
  right: 0;
  width: 350px;
  height: 500px;
  background: #4b2e83;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(20px);
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.8)'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 300px;
    height: 450px;
    bottom: 70px;
  }
  
  @media (max-width: 480px) {
    width: calc(100vw - 30px);
    right: -10px;
  }
`;

const ChatHeader = styled.div`
  background: #4b2e83;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
`;

const ChatHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChatAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.3);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`;

const ChatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ChatTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const ChatStatus = styled.p`
  margin: 0;
  font-size: 12px;
  opacity: 0.8;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ChatBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 15px;
`;

const WelcomeMessage = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const MessageBubble = styled.div`
  background: ${props => props.isUser ? '#4b2e83' : '#f8f9ff'};
  color: ${props => props.isUser ? 'white' : '#333'};
  padding: 12px 16px;
  border-radius: ${props => props.isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px'};
  max-width: 80%;
`;

const MessageText = styled.p`
  margin: 0;
  font-size: 14px;
  color: inherit;
  line-height: 1.4;
`;

const MessageTime = styled.span`
  font-size: 11px;
  color: ${props => props.isUser ? 'rgba(255, 255, 255, 0.7)' : '#999'};
  margin-top: 5px;
  display: block;
`;

const QuickActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const QuickActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(75, 46, 131, 0.1);
  border: 1px solid rgba(75, 46, 131, 0.2);
  border-radius: 20px;
    color: #4b2e83;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(75, 46, 131, 0.15);
    transform: translateY(-1px);
  }
  
  svg {
    font-size: 14px;
  }
`;

const ChatInput = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
  margin-top: auto;
`;

const MessageInput = styled.textarea`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid rgba(75, 46, 131, 0.2);
  border-radius: 20px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  max-height: 100px;
  min-height: 40px;
  background: #f8f8ff;
  color: #333;
  
  &:focus {
    outline: none;
    border-color: #4b2e83;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f8f8ff;
  border: none;
  color: #4b2e83;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmailForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PinStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PinMessage = styled.p`
  margin: 0;
  font-size: 13px;
  color: #f8f8ff;
  line-height: 1.4;
  strong { color: #fff; }
`;

const PinDisplay = styled.div`
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 4px;
  color: #fff;
  text-align: center;
`;

const PinInput = styled.input`
  padding: 12px 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  font-size: 14px;
  font-family: inherit;
  background: #f8f8ff;
  color: #333;
  letter-spacing: 2px;
  &:focus {
    outline: none;
    border-color: #dda61f;
  }
  &::placeholder { color: #999; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #ffb3b3;
`;

const EmailInput = styled.input`
  padding: 12px 16px;
  border: 2px solid rgba(75, 46, 131, 0.2);
  border-radius: 20px;
  font-size: 14px;
  font-family: inherit;
  background: #f8f8ff;
  color: #333;
  
  &:focus {
    outline: none;
    border-color: #4b2e83;
  }
  
  &::placeholder {
    color: #999;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StartChatButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: #f8f8ff;
  border: none;
  border-radius: 20px;
  color: #4b2e83;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(75, 46, 131, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(75, 46, 131, 0.3);
    border-radius: 2px;
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  justify-content: ${props => props.isUser ? 'flex-end' : 'flex-start'};
`;

const TypingIndicator = styled.div`
  background: #f8f9ff;
  padding: 12px 16px;
  border-radius: 18px 18px 18px 4px;
  max-width: 80px;
`;

const TypingDots = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 5px;
  
  span {
    width: 6px;
    height: 6px;
    background: #4b2e83;
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out;
    
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
    &:nth-child(3) { animation-delay: 0s; }
  }
  
  @keyframes typing {
    0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
    40% { transform: scale(1); opacity: 1; }
  }
`;
